import { useEffect, useRef, useState } from "react";
import { ArrowRight, Gift, Pause, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE_URL } from "../../config/site";
import SEO from "../../components/SEO";
import StructuredData from "../../components/StructuredData";
import SeasonalCampaign from "../../components/SeasonalCampaign";
import FeaturedReviews from "../../components/FeaturedReviews";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import "./HomePage.css";

const FRAME_FEATURE = {
  eyebrow: "Modelos y colores",
  title: "Marcos para imanes personalizados",
  text: "Elige entre marcos individuales, Duo, Mosaico o Secuencia y prueba colores antes de comprar. Una forma visual de exponer tus imanes en la nevera, superficies metálicas o con soporte.",
  imageSrc:
    "/images/frame-gallery/frame-gallery-individuales/frame-gallery-marcos.webp",
  imageAlt: "Marcos para imanes personalizados de distintos formatos y colores sobre una nevera",
  imageWidth: 1600,
  imageHeight: 1000,
  link: "/marcos-para-imanes-personalizados",
  cta: "Ver todos los marcos",
  layout: ["image", "text"],
};

const OFFER_FEATURE = {
  eyebrow: "Pack edición especial",
  title: "Oferta Pack Recuerdos de Cine",
  text: "Oferta completa con tres imanes personalizados, una tira de negativos y soporte incluidos. Un pack llamativo, listo para sorprender y con precio especial.",
  imageSrc:
    "/images/frame-gallery/frame-gallery-tira-negativos/frame-gallery-tira-negativos-poster.webp",
  imageAlt: "Pack Recuerdos de Cine con tres imanes personalizados en una tira de negativos",
  imageWidth: 1920,
  imageHeight: 1080,
  link: "/ofertas",
  cta: "Ver oferta",
  layout: ["image", "text"],
};

const HERO_CARDS = [
  {
    id: "colecciones",
    eyebrow: "Para regalar",
    title: "Recuerdos con historia",
    text: "Crea pequeñas colecciones con fotos familiares, viajes o momentos especiales.",
    mediaType: "image",
    mediaSrc:
      "/images/frame-gallery/frame-gallery-imanes/iman-personalizado-niña-perro-slider.webp",
    mediaAlt: "Colección de imanes personalizados con la foto de una niña y su perro",
    mediaWidth: 1920,
    mediaHeight: 1080,
    priority: true,
    link: "/imanes-personalizados-con-fotos",
  },
  {
    id: "video",
    eyebrow: "Hechos a mano",
    title: "Imanes personalizados",
    text: "Tus fotos favoritas en imanes artesanales, protegidos y listos para regalar.",
    mediaType: "video",
    mediaSrc: "/videos/home.mp4",
    mediaPoster:
      "/images/frame-gallery/frame-gallery-imanes/iman-personalizado-piscina.webp",
    mediaAlt: "Vídeo mostrando imanes personalizados con fotos",
    mediaWidth: 1920,
    mediaHeight: 1080,
    link: "/imanes-personalizados-con-fotos",
  },
  {
    id: "collage",
    eyebrow: "Collage magnético",
    title: "Una foto, un collage de imanes",
    text: "Divide tu imagen en 4, 9, 12, 15, 16 o 20 imanes y prueba el resultado antes de comprar.",
    mediaType: "image",
    mediaSrc:
      "/images/frame-gallery/frame-gallery-collage/frame-gallery-collage.3.webp",
    mediaAlt: "Collage magnético personalizado con una fotografía dividida en varios imanes",
    mediaWidth: 1920,
    mediaHeight: 1080,
    link: "/collage-magnetico-personalizado#collage-editor",
    cta: "Crear mi collage",
  },
];

const HALLOWEEN_FEATURE = {
  eyebrow: "Colección Halloween",
  title: "Recuerdos de miedo. De los buenos.",
  text: "Calabaza, sombrero de bruja, fantasma y telaraña con araña morada. Descubre los cuatro marcos de Halloween y prueba cómo quedan con tus fotos.",
  imageSrc: "/images/frame-gallery/frame-gallery-halloween/frame-gallery-halloween-todos.webp",
  imageAlt: "Los cuatro marcos de Halloween con fotos: calabaza, bruja, fantasma y telaraña",
  imageWidth: 2024,
  imageHeight: 2024,
  link: "/marcos-individuales",
  cta: "Descubrir Halloween",
  halloween: true,
  layout: ["image", "text"],
};

const CREATION_FEATURES = [HALLOWEEN_FEATURE, OFFER_FEATURE, FRAME_FEATURE];

const HOME_TRUST_ITEMS = [
  "Fabricados por nosotros",
  "Producción de 2 a 5 días",
  "Pago seguro con Stripe",
  "Envío peninsular 4,95 €",
];

function scheduleWhenIdle(callback) {
  if ("requestIdleCallback" in window) {
    const idleId = window.requestIdleCallback(callback, { timeout: 1800 });
    return () => window.cancelIdleCallback(idleId);
  }

  const timeoutId = window.setTimeout(callback, 1200);
  return () => window.clearTimeout(timeoutId);
}

function HeroCardContent({ card }) {
  return (
    <>
      <span>{card.eyebrow}</span>
      <h3>{card.title}</h3>
      <p>{card.text}</p>
      <strong>{card.cta || "Crear imanes"}</strong>
    </>
  );
}

function HeroVideoCard({ card }) {
  const videoRef = useRef(null);
  const [sourceEnabled, setSourceEnabled] = useState(false);
  const [wantsPlayback, setWantsPlayback] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const isPrerender = new URLSearchParams(window.location.search).has(
      "prerender",
    );

    if (isPrerender) return undefined;

    const desktopQuery = window.matchMedia("(min-width: 701px)");
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    let cancelScheduledLoad = () => {};

    const applyPlaybackPreference = () => {
      cancelScheduledLoad();

      if (desktopQuery.matches && !reducedMotionQuery.matches) {
        cancelScheduledLoad = scheduleWhenIdle(() => {
          setSourceEnabled(true);
          setWantsPlayback(true);
        });
        return;
      }

      setWantsPlayback(false);
      videoRef.current?.pause();
    };

    applyPlaybackPreference();
    desktopQuery.addEventListener("change", applyPlaybackPreference);
    reducedMotionQuery.addEventListener("change", applyPlaybackPreference);

    return () => {
      cancelScheduledLoad();
      desktopQuery.removeEventListener("change", applyPlaybackPreference);
      reducedMotionQuery.removeEventListener(
        "change",
        applyPlaybackPreference,
      );
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !sourceEnabled) return undefined;

    if (!wantsPlayback) {
      video.pause();
      return undefined;
    }

    let active = true;
    const startPlayback = () => {
      const playback = video.play();

      if (playback) {
        playback.catch(() => {
          if (active) setWantsPlayback(false);
        });
      }
    };

    if (video.readyState >= 2) startPlayback();
    else video.addEventListener("canplay", startPlayback, { once: true });

    return () => {
      active = false;
      video.removeEventListener("canplay", startPlayback);
    };
  }, [sourceEnabled, wantsPlayback]);

  const togglePlayback = () => {
    if (isPlaying) {
      setWantsPlayback(false);
      videoRef.current?.pause();
      return;
    }

    setSourceEnabled(true);
    setWantsPlayback(true);
  };

  return (
    <article className="home-create-item home-create-item--video">
      <div className="home-create-media">
        <video
          ref={videoRef}
          src={sourceEnabled ? card.mediaSrc : undefined}
          poster={card.mediaPoster}
          width={card.mediaWidth}
          height={card.mediaHeight}
          muted
          loop
          playsInline
          preload={sourceEnabled ? "metadata" : "none"}
          aria-label={card.mediaAlt}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
        <button
          type="button"
          className="home-video-toggle"
          onClick={togglePlayback}
          aria-label={isPlaying ? "Pausar vídeo" : "Reproducir vídeo"}
          aria-pressed={isPlaying}
        >
          {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </button>
      </div>

      <Link to={card.link} className="home-create-content home-create-content-link">
        <HeroCardContent card={card} />
      </Link>
    </article>
  );
}

function HeroCard({ card }) {
  if (card.mediaType === "video") {
    return <HeroVideoCard card={card} />;
  }

  return (
    <Link to={card.link} className="home-create-item">
      <div className="home-create-media">
        <img
          src={card.mediaSrc}
          alt={card.mediaAlt}
          width={card.mediaWidth}
          height={card.mediaHeight}
          loading={card.priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={card.priority ? "high" : "auto"}
        />
      </div>

      <div className="home-create-content">
        <HeroCardContent card={card} />
      </div>
    </Link>
  );
}

function renderFeatureSlot(feature, slot) {
  if (slot === "image") {
    return (
      <Link
        key="image"
        to={feature.link}
        className={`home-feature-media home-feature-media--image${feature.halloween ? " home-feature-media--halloween" : ""}`}
      >
        <img
          src={feature.imageSrc}
          alt={feature.imageAlt}
          width={feature.imageWidth}
          height={feature.imageHeight}
          loading="lazy"
          decoding="async"
        />
      </Link>
    );
  }

  return (
    <div key="text" className="home-feature-content home-feature-content--text">
      <span>{feature.eyebrow}</span>
      <p>{feature.text}</p>
      <Link to={feature.link}>
        {feature.cta}
        {feature.link === "/ofertas" ? (
          <Gift aria-hidden="true" />
        ) : (
          <ArrowRight aria-hidden="true" />
        )}
      </Link>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="home-page">
      <SEO
        title="Regalos personalizados con fotos | Imalandia"
        description="Crea regalos personalizados con tus fotos: imanes, marcos, collages y composiciones artesanales para regalar, decorar y conservar tus recuerdos."
        canonical={`${SITE_URL}/`}
        image="/images/frame-gallery/frame-gallery-imanes/iman-personalizado-colecciones-slider.webp"
        imageAlt="Colecciones de imanes personalizados creados con fotografías, ilustraciones y logotipos"
        imageWidth={1920}
        imageHeight={1080}
      />
      <StructuredData
        data={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Imalandia",
            url: SITE_URL,
            logo: `${SITE_URL}/images/branding/logo_imalandia.png`,
            description:
              "Regalos, imanes, collages y marcos personalizados creados artesanalmente a partir de fotografías.",
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer service",
              availableLanguage: "es",
              url: `${SITE_URL}/contacto`,
            },
            sameAs: [
              "https://www.instagram.com/imalandia.com.es/",
              "https://tiktok.com/@imalandia.com",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Imalandia",
            url: SITE_URL,
            inLanguage: "es-ES",
          },
        ]}
      />

      <a className="home-skip-link" href="#home-main">
        Saltar al contenido principal
      </a>

      <Header />

      <main id="home-main" tabIndex="-1">
      <section className="home-hero" aria-labelledby="home-title">
        <div className="home-hero-content">
          <p className="home-hero-eyebrow">Recuerdos para regalar y decorar</p>
          <h1 id="home-title">Regalos personalizados con tus fotos</h1>
          <p className="home-hero-text">
            Crea imanes, marcos y composiciones con tus momentos favoritos:
            piezas originales para sorprender o tener tus recuerdos siempre a
            la vista.
          </p>

          <div className="home-hero-actions">
            <Link
              to="/imanes-personalizados-con-fotos"
              className="home-hero-primary"
            >
              Ver imanes personalizados
              <ArrowRight aria-hidden="true" />
            </Link>

            <a href="#home-creation-title" className="home-hero-secondary">
              Explorar creaciones
            </a>
          </div>

          <ul className="home-hero-confidence" aria-label="Información de compra">
            {HOME_TRUST_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <h2 id="home-hero-cards-title" className="home-visually-hidden">
          Ideas para crear con tus fotos
        </h2>

        <div
          className="home-create-grid home-hero-grid"
          aria-labelledby="home-hero-cards-title"
        >
          {HERO_CARDS.map((card) => (
            <HeroCard key={card.id} card={card} />
          ))}
        </div>
      </section>

      <SeasonalCampaign />

      <section
        className="home-creation-showcase"
        aria-labelledby="home-creation-title"
      >
        <header className="home-band-heading home-creation-heading">
          <p>Escaparate creativo</p>
          <h2 id="home-creation-title">Crea con tus fotos</h2>
          <span>
            Una selección breve para llegar rápido a nuestras ofertas y a la
            colección de marcos personalizados.
          </span>
        </header>

        <div className="home-creation-list">
          {CREATION_FEATURES.map((feature) => (
            <article key={feature.link} className={`home-creation-row${feature.link === "/ofertas" ? " home-creation-row--offer" : ""}${feature.halloween ? " home-creation-row--halloween" : ""}`}>
              <h3 className="home-creation-row-title">{feature.title}</h3>

              <div className="home-feature-row">
                {feature.layout.map((slot) => renderFeatureSlot(feature, slot))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="home-shop-cta" aria-labelledby="home-shop-title">
        <div className="home-shop-cta-media">
          <img
            src="/images/frame-gallery/frame-gallery-portalapices/frame-gallery-soportes-escritorio-novios.webp"
            alt="Portalápices negro con cuatro imanes personalizados de una pareja"
            width="900"
            height="900"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="home-shop-cta-content">
          <p>Tienda Imalandia</p>
          <h2 id="home-shop-title">Completa tus recuerdos</h2>
          <span>
            Descubre soportes, expositores y accesorios para colocar, regalar y
            disfrutar tus imanes personalizados.
          </span>
          <Link to="/tienda">
            Ver la tienda
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section
        id="como-funciona"
        className="home-section"
        aria-labelledby="home-process-title"
      >
        <header className="home-band-heading">
          <p>Proceso sencillo</p>
          <h2 id="home-process-title" className="home-section-title">
            Lo preparamos con cuidado
          </h2>
          <span>
            Tú eliges las fotos y nosotros fabricamos cada pieza con acabado
            protegido, lista para regalar o decorar.
          </span>
        </header>

        <div className="home-steps">
          <div className="home-step">
            <strong>1</strong>
            <h3>Elige qué quieres crear</h3>
            <p>
              Imanes, marcos, collage o packs especiales según el tipo de
              recuerdo que tengas en mente.
            </p>
          </div>

          <div className="home-step">
            <strong>2</strong>
            <h3>Personaliza tus fotos</h3>
            <p>
              Sube tus imágenes y prueba encuadres, colores, modelos y formatos
              antes de añadir al carrito.
            </p>
          </div>

          <div className="home-step">
            <strong>3</strong>
            <h3>Lo fabricamos para ti</h3>
            <p>
              Preparamos el pedido de forma artesanal, con protección sobre la
              imagen y atención al detalle.
            </p>
          </div>
        </div>
      </section>

      <FeaturedReviews />
      </main>

      <Footer />
    </div>
  );
}
